import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {PropsWithChildren} from 'react'
import {useEventDateInformation} from '../../../../../../commons/hooks/dates'
import {useSettings} from '../../../../hooks/settings'
import {settingsParams} from '../../../../settingsParams'
import {useVisibilityStyles} from '../../../hooks/use-visibility-styles'
import {ClosedRegistrationButton} from '../../registration-buttons/closed-registration-button'
import {EditRegistrationButton} from '../../registration-buttons/edit-registration-button'
import {RegistrationButtonSkin} from '../../registration-buttons/registration-button-skin'
import {TicketsUnavailableButton} from '../../registration-buttons/tickets-unavailable-button'
import {CustomDatesLabel} from '../custom-dates-label'
import {RegistrationButtonProps} from './interfaces'
import s from './registration-button.scss'

export const RegistrationButton = ({
  disableSpacing,
  ticketedOrInitiallyTicketedEvent,
  registrationClosed,
  memberRsvpExists,
  onClick,
  imageVisible,
  ticketsUnavailable,
  className,
  url,
  isExternalLink,
  withoutRegistration,
  registrationScheduled,
  registrationWithCustomDates,
  eventId,
  t,
  eventListPageUrl,
  narrowerButton,
}: RegistrationButtonProps) => {
  const {formattedRegistrationStartDate: startDate, formattedRegistrationEndDate: endDate} =
    useEventDateInformation(eventId)
  const {
    allBreakpoints: {isHeaderRegistrationButtonVisible},
  } = useVisibilityStyles()
  const settings = useSettings()
  const {isMobile} = useEnvironment()

  const rsvpButtonText = settings.getWithFallback(
    ticketedOrInitiallyTicketedEvent ? settingsParams.ticketedEventButtonText : settingsParams.rsvpEventButtonText,
    settingsParams.rsvpButtonText,
  )

  if (!isHeaderRegistrationButtonVisible()) {
    return null
  }

  const getButton = () => {
    if (registrationScheduled) {
      return (
        <>
          <ClosedRegistrationButton href={eventListPageUrl} />
          <CustomDatesLabel text={t('registration.opensLabel', {date: startDate})} dataHook={DH.RSVP_OPENS_LABEL} />
        </>
      )
    }

    if (ticketsUnavailable) {
      return <TicketsUnavailableButton href={eventListPageUrl} />
    }

    if (registrationClosed) {
      return <ClosedRegistrationButton href={eventListPageUrl} />
    }

    if (memberRsvpExists) {
      return <EditRegistrationButton />
    }

    if (withoutRegistration) {
      return null
    }

    return (
      <>
        <RegistrationButtonSkin
          text={rsvpButtonText}
          onClick={onClick}
          url={url}
          isExternalLink={isExternalLink}
          dataHook={ticketedOrInitiallyTicketedEvent ? DH.HEADER_GET_TICKETS_BUTTON : DH.RSVP_BUTTON}
          narrowerButton={narrowerButton}
        />
        {registrationWithCustomDates && (
          <CustomDatesLabel text={t('registration.closesLabel', {date: endDate})} dataHook={DH.RSVP_CLOSES_LABEL} />
        )}
      </>
    )
  }

  const button = getButton()

  return button ? (
    <ButtonContainer
      hasImageButtonSpacing={isMobile || imageVisible}
      disableSpacing={disableSpacing}
      className={classNames(s.container, className)}
    >
      {button}
    </ButtonContainer>
  ) : null
}

interface ButtonContainerProps {
  disableSpacing: boolean
  hasImageButtonSpacing: boolean
  className: string
}

const ButtonContainer = ({disableSpacing, className, children}: PropsWithChildren<ButtonContainerProps>) => {
  const spacingClass = s.registrationButtonSpacing
  return <div className={classNames({[spacingClass]: !disableSpacing}, className)}>{children}</div>
}
